// Session-level calorie ESTIMATE (HC-5 Slice B). Pure, IO-free, no zod — the
// bridge between a logged workout and the atomic-bout physiology in
// calorieEstimate.ts. It decomposes ONE session into calorie "bouts", estimates
// each, and aggregates them into a single honest figure the save-path persists
// beside the Health Connect value (never overwriting it).
//
// Bout model (next/health-connect-cgm.md §HC-5 "session duration, per-cardio-
// set duration"):
//   • Each CARDIO set with a real duration is its OWN bout — its per-set minutes
//     + its own (or the session's) average HR, so a 40-min run and a 5-min
//     finisher aren't smeared into one MET guess.
//   • The STRENGTH remainder is ONE bout spanning the session's leftover time
//     (total session minutes − the cardio minutes, floored at 0). Strength
//     energy cost is coarse regardless (calorieEstimate.ts), so per-exercise
//     splitting would be false precision; one bout keeps the time-accounting
//     honest (no double-counting the clock across strength + cardio).
//
// Aggregation stays honest: `method` is keytel if ANY bout used measured HR
// (the better route), else met; `confidence` is `high` ONLY when every
// contributing bout is high (a pure cardio-with-HR session) — a single
// low-confidence bout (any strength, or a MET fallback) drags the whole
// estimate to `low`, so the UI/coach never over-trusts a mixed number.

import {
  cardioMetCategory,
  estimateBoutCalories,
  REST_MET,
  strengthSetWindow,
  type AthleteEnergy,
  type CalorieBout,
  type CalorieEstimate,
} from "./calorieEstimate.js";
import { sessionDurationMin } from "./duration.js";
import { effectiveRir } from "./effortScale.js";
import type { LogSetKind } from "./schemas/logSet.js";
import type { MovementPattern } from "./schemas/muscle.js";
import type { WorkoutExerciseEntry } from "./schemas/workout.js";

// One logged set as the estimator needs to see it. `movementPattern` is the
// exercise's taxonomy signal (resolved by the caller from the catalog at save
// time); it only gates the cardio-vs-strength split here — a "cardio" set is
// priced by cardioMetCategory, everything else by the rest-aware strength model.
// `durationSec` is the cardio/timed set's measured duration; `avgHrBpm` is the
// set's own HR when a per-set value exists (else the session's is used).
export type SessionCalorieSet = {
  exerciseSlug: string;
  movementPattern?: MovementPattern;
  durationSec?: number;
  avgHrBpm?: number;
  // The set's own logging kind — carried verbatim from both mappers (LogSet.kind
  // / WorkoutSet.type) so "is this a static hold" is read from the FACT, never
  // re-inferred from reps/duration presence (which misreads a loaded-carry, a
  // legitimately reps-less, duration-carrying dynamic movement, as a hold).
  kind?: LogSetKind;
  // Strength-only inputs for the rest-aware model: reps (or a hold/carry's
  // durationSec) size the elevated window; rir/rpe/fail size its recovery tail
  // (resolved to an effective RIR via effortScale.effectiveRir). Absent on cardio.
  // `rpe` rides alongside `rir` because effectiveRir falls back to it (MX-4): a
  // set logged in the RPE scale (Leonardo, and every pre-single-column row)
  // carries ONLY rpe, and dropping it here would price a real effort at the
  // untagged default (MX-4a).
  reps?: number;
  rir?: number;
  rpe?: number;
  fail?: boolean;
};

// The session shape the estimate needs — a thin structural subset, NOT the zod
// Session (keeps this module pure and callable from any layer). `avgHrBpm` is
// the whole-session HR (HC enrichment); each set may carry its own.
export type SessionCalorieInput = {
  startedAt: string;
  endedAt?: string;
  avgHrBpm?: number;
  sets: ReadonlyArray<SessionCalorieSet>;
};

const isCardio = (s: SessionCalorieSet): boolean =>
  s.movementPattern === "cardio";

// Coarse work-time per rep, shared by the rest-aware strength model and the LIVE
// projection so the two never drift on "how long was a set under load".
export const SECONDS_PER_REP = 3;

// One set's time under load, in seconds: a hold's own seconds, else
// reps × SECONDS_PER_REP. The ONE place that maps (reps, durationSec) → work
// time — reused by estimateSessionCalories (strength windows) and
// projectSessionMinutes (live projection). Returns 0 when neither is present
// (e.g. a loaded-carry logged without reps — it still gets its recovery tail).
export const setWorkSec = (
  reps: number | undefined,
  durationSec: number | undefined,
): number =>
  durationSec && durationSec > 0
    ? durationSec
    : reps && reps > 0
      ? reps * SECONDS_PER_REP
      : 0;

/**
 * Estimate a whole session's calories, or null when nothing can be estimated
 * honestly (no weight, or neither a cardio duration nor a timed session to size
 * a strength bout). The athlete's demographics come from resolveAthleteEnergy.
 */
export const estimateSessionCalories = (
  input: SessionCalorieInput,
  athlete: AthleteEnergy,
): CalorieEstimate | null => {
  const bouts: CalorieBout[] = [];

  // Cardio sets → per-set bouts (own minutes + own/session HR).
  let cardioMin = 0;
  for (const s of input.sets) {
    if (!isCardio(s) || !(s.durationSec && s.durationSec > 0)) continue;
    const durationMin = s.durationSec / 60;
    cardioMin += durationMin;
    bouts.push({
      durationMin,
      category: cardioMetCategory(s.exerciseSlug),
      avgHrBpm: s.avgHrBpm ?? input.avgHrBpm,
    });
  }

  // Strength → per-set ELEVATED WINDOWS (lift + recovery tail) + one idle
  // remainder near resting, over the session's leftover time. NO avgHrBpm on
  // these bouts: strength deliberately ignores HR (an intermittent session's
  // mean HR drove Keytel to ~2× a watch); the rest-aware split IS the estimate.
  const strengthSets = input.sets.filter((s) => !isCardio(s));
  const sessionMin = sessionDurationMin(input.startedAt, input.endedAt);
  if (strengthSets.length > 0 && sessionMin !== null) {
    const strengthMin = Math.max(0, sessionMin - cardioMin);
    if (strengthMin > 0) {
      const windows = strengthSets.map((s) =>
        strengthSetWindow(
          setWorkSec(s.reps, s.durationSec),
          effectiveRir(s),
          s.kind === "timed", // a static hold — read from the kind, not inferred
        ),
      );
      // Cap the elevated windows at the available strength time; if they overrun
      // a short session, scale them down proportionally (idle = 0) rather than
      // spending more clock than the session actually had.
      const elevatedMin = windows.reduce((sum, w) => sum + w.durationMin, 0);
      const scale = elevatedMin > strengthMin ? strengthMin / elevatedMin : 1;
      for (const w of windows) {
        bouts.push({ durationMin: w.durationMin * scale, category: w.category });
      }
      const idleMin = Math.max(0, strengthMin - elevatedMin);
      if (idleMin > 0) {
        bouts.push({
          durationMin: idleMin,
          category: { kind: "strength", met: REST_MET },
        });
      }
    }
  }

  const results = bouts
    .map((b) => estimateBoutCalories(b, athlete))
    .filter((e): e is CalorieEstimate => e !== null);
  if (results.length === 0) return null;

  const kcal = results.reduce((sum, e) => sum + e.kcal, 0);
  const method = results.some((e) => e.method === "keytel") ? "keytel" : "met";
  const confidence = results.every((e) => e.confidence === "high")
    ? "high"
    : "low";
  return { kcal, method, confidence };
};

/**
 * Estimate straight from a logged workout — the save-path adapter. Flattens the
 * workout's entries into per-set bouts (the entry's `movementPattern` comes from
 * `patterns`, keyed by exerciseId, since the Workout schema doesn't carry it),
 * pulling per-set duration/HR off cardio sets and reps/rir/fail off strength
 * sets (the rest-aware model sizes each set's elevated window + recovery tail).
 * There is no session-level HR at save time (Health Connect enriches that
 * later), and strength ignores HR anyway, so a fresh save is already the final
 * strength estimate; only a cardio set that logged its own HR takes Keytel.
 * Returns null when nothing is estimable.
 */
export const estimateWorkoutCalories = (
  workout: {
    startedAt: string;
    endedAt?: string;
    entries: ReadonlyArray<Pick<WorkoutExerciseEntry, "exerciseId" | "sets">>;
  },
  patterns: Readonly<Record<string, MovementPattern | null | undefined>>,
  athlete: AthleteEnergy,
): CalorieEstimate | null => {
  const sets: SessionCalorieSet[] = [];
  for (const entry of workout.entries) {
    const movementPattern = patterns[entry.exerciseId] ?? undefined;
    for (const s of entry.sets) {
      // Duration is work time for a hold (timed) or a carry (loaded-carry), and
      // the bout length for cardio — carry it for all three (matches the API
      // enrichment mapper, so save-time and enrich agree on every set).
      const durationSec =
        s.type === "cardio" || s.type === "timed" || s.type === "loaded-carry"
          ? s.duration
          : undefined;
      const avgHrBpm = s.type === "cardio" ? s.avgHrBpm : undefined;
      const reps = "reps" in s ? s.reps : undefined;
      const rir = "rir" in s ? s.rir : undefined;
      // rpe travels with rir — effectiveRir derives the canonical RIR from it
      // when rir is absent (MX-4). Stripping it here made an rpe-only set fall
      // to the untagged effort default (MX-4a).
      const rpe = "rpe" in s ? s.rpe : undefined;
      const fail = "fail" in s ? s.fail : undefined;
      sets.push({
        exerciseSlug: entry.exerciseId,
        movementPattern,
        kind: s.type,
        durationSec,
        avgHrBpm,
        reps,
        rir,
        rpe,
        fail,
      });
    }
  }
  return estimateSessionCalories(
    { startedAt: workout.startedAt, endedAt: workout.endedAt, sets },
    athlete,
  );
};

/**
 * Projected duration (minutes) of an in-progress / planned workout, derived from
 * its set STRUCTURE rather than the clock — there is no `endedAt` mid-workout.
 * Each set contributes its work time (a cardio/timed set's own duration, else
 * reps × SECONDS_PER_REP) plus one rest interval. Deliberately coarse (Antenore
 * 2026-07-10: "una formula con un arrotondamento più che ragionevole"); it
 * re-projects as sets/exercises change, so an empty draft and a pre-filled
 * program yield different numbers.
 */
export const projectSessionMinutes = (
  workout: { entries: ReadonlyArray<Pick<WorkoutExerciseEntry, "sets">> },
  restSec: number,
): number => {
  let sec = 0;
  for (const entry of workout.entries) {
    for (const s of entry.sets) {
      // A set with no reps and no duration (e.g. a loaded-carry logged without
      // reps) contributes only its rest interval — acceptably coarse.
      const durationSec =
        s.type === "cardio" || s.type === "timed" ? s.duration : undefined;
      const reps = "reps" in s && typeof s.reps === "number" ? s.reps : undefined;
      sec += setWorkSec(reps, durationSec) + restSec;
    }
  }
  return sec / 60;
};

/**
 * Live calorie estimate for an in-progress workout. Since there is no `endedAt`
 * yet (the strength bout would otherwise be skipped), synthesize one from the
 * projected duration and reuse the exact save-path estimator — so the live
 * number and the persisted-at-save number come from the SAME machinery, only
 * differing by projected-vs-actual duration.
 */
export const estimateLiveWorkoutCalories = (
  workout: {
    startedAt: string;
    entries: ReadonlyArray<Pick<WorkoutExerciseEntry, "exerciseId" | "sets">>;
  },
  patterns: Readonly<Record<string, MovementPattern | null | undefined>>,
  athlete: AthleteEnergy,
  restSec: number,
): CalorieEstimate | null => {
  const minutes = projectSessionMinutes(workout, restSec);
  if (!(minutes > 0)) return null;
  const endedAt = new Date(
    Date.parse(workout.startedAt) + Math.round(minutes * 60_000),
  ).toISOString();
  return estimateWorkoutCalories({ ...workout, endedAt }, patterns, athlete);
};
